import type {
  FixedRoute,
  FixedRouteSegment,
  FixedRouteStop,
  FixedRouteStopConfig,
  GtfsAgency,
  RouteGeometry,
  ServiceDaysMask,
  ServiceExceptionType,
} from '../../types/fixedRoutes';
import { getDistanceMeters } from '../../components/map/utils/distance';
import { computeRouteGeometryHash, generateRouteColor, generateRouteColors } from './routeToLineConverter';
import { GTFS_CORE_FILES, GTFS_OPTIONAL_FILES, resolveGtfsZipEntries } from './gtfsZipUtils';

type StringBoolean = '0' | '1' | '';

export type ServicePreference = 'weekday' | 'saturday' | 'sunday';

export interface GTFSStopsRow {
  stop_id: string;
  stop_name?: string;
  stop_lat?: string;
  stop_lon?: string;
  stop_desc?: string;
}

export interface GTFSRoutesRow {
  route_id: string;
  agency_id?: string;
  route_short_name?: string;
  route_long_name?: string;
  route_desc?: string;
  route_type?: string;
  route_color?: string;
  route_text_color?: string;
}

export interface GTFSAgencyRow {
  agency_id?: string;
  agency_name?: string;
  agency_url?: string;
  agency_timezone?: string;
  agency_lang?: string;
}

export interface GTFSTripRow {
  trip_id: string;
  route_id: string;
  service_id: string;
  shape_id?: string;
  trip_headsign?: string;
  direction_id?: string;
}

export interface GTFSStopTimeRow {
  trip_id: string;
  stop_id: string;
  stop_sequence: string;
  arrival_time?: string;
  departure_time?: string;
  /** Distance along the trip shape from its first shape point, in meters (GTFS). */
  shape_dist_traveled?: string;
}

export interface GTFSShapeRow {
  shape_id: string;
  shape_pt_lat: string;
  shape_pt_lon: string;
  shape_pt_sequence: string;
  /** Cumulative distance along the shape from its first point, in meters (GTFS). */
  shape_dist_traveled?: string;
}

export interface GTFSCalendarRow {
  service_id: string;
  monday: StringBoolean;
  tuesday: StringBoolean;
  wednesday: StringBoolean;
  thursday: StringBoolean;
  friday: StringBoolean;
  saturday: StringBoolean;
  sunday: StringBoolean;
  start_date?: string;
  end_date?: string;
}

export interface GTFSCalendarDateRow {
  service_id: string;
  date: string;
  exception_type: '1' | '2';
}

export interface GTFSFeedData {
  stops: GTFSStopsRow[];
  routes: GTFSRoutesRow[];
  trips: GTFSTripRow[];
  stopTimes: GTFSStopTimeRow[];
  shapes: GTFSShapeRow[];
  calendar?: GTFSCalendarRow[];
  calendarDates?: GTFSCalendarDateRow[];
  agency?: GTFSAgencyRow[];
}

export interface GTFSConversionOptions {
  preferredService?: ServicePreference;
  targetServiceId?: string;
}

export interface GTFSConverterResult {
  stops: FixedRouteStop[];
  routes: FixedRoute[];
  serviceIdUsed: string | null;
  warnings: string[];
  stats: {
    totalRoutes: number;
    totalStops: number;
    skippedRoutes: number;
  };
}

export interface ServiceWindowSummary {
  serviceId: string;
  label: string;
  activeDays: string[];
  preference: ServicePreference | 'custom';
  startDate?: string;
  endDate?: string;
  addedDates?: number;
  removedDates?: number;
}

const DAY_ORDER: Array<{ key: keyof GTFSCalendarRow; label: string }> = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' },
];

const minutesFormatter = (minutes: number | null | undefined): string => {
  if (minutes === null || minutes === undefined || Number.isNaN(minutes)) {
    return '00:00';
  }
  const hours = Math.floor(minutes / 60);
  const mins = Math.abs(Math.round(minutes % 60));
  const normalizedHours = ((hours % 24) + 24) % 24;
  return `${normalizedHours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
};

const parseTimeToMinutes = (time?: string): number | null => {
  if (!time) return null;
  const parts = time.split(':');
  if (parts.length < 2) return null;
  const hours = Number(parts[0]);
  const minutes = Number(parts[1]);
  const seconds = parts.length > 2 ? Number(parts[2]) : 0;
  if ([hours, minutes, seconds].some((value) => Number.isNaN(value))) {
    return null;
  }
  return hours * 60 + minutes + Math.floor(seconds / 60);
};

const normalizeWraparoundTravelMinutes = (departure: number, arrival: number): number => {
  let diff = arrival - departure;
  if (diff < 0) {
    // Some feeds wrap to 00:xx instead of using 24+:xx for after-midnight service.
    diff += 24 * 60;
  }
  return diff;
};

const parseLatLng = (value?: string): number | null => {
  if (!value) return null;
  const num = Number(value);
  if (Number.isNaN(num)) return null;
  return num;
};

const sanitizeColor = (color?: string): string | undefined => {
  if (!color) return undefined;
  const cleaned = color.trim().replace('#', '');
  if (!cleaned.match(/^[0-9a-f]{6}$/i)) {
    return undefined;
  }
  return `#${cleaned.toUpperCase()}`;
};

const toPreference = (calendarRow: GTFSCalendarRow): ServicePreference | 'custom' => {
  const weekdayActive =
    calendarRow.monday === '1' &&
    calendarRow.tuesday === '1' &&
    calendarRow.wednesday === '1' &&
    calendarRow.thursday === '1' &&
    calendarRow.friday === '1';
  const saturdayOnly =
    calendarRow.saturday === '1' &&
    calendarRow.sunday !== '1' &&
    ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']
      .map((day) => (calendarRow[day as keyof GTFSCalendarRow] as StringBoolean) === '0')
      .every(Boolean);
  const sundayOnly =
    calendarRow.sunday === '1' &&
    calendarRow.saturday !== '1' &&
    ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']
      .map((day) => (calendarRow[day as keyof GTFSCalendarRow] as StringBoolean) === '0')
      .every(Boolean);

  if (weekdayActive && calendarRow.saturday === '0' && calendarRow.sunday === '0') return 'weekday';
  if (saturdayOnly) return 'saturday';
  if (sundayOnly) return 'sunday';
  return 'custom';
};

const buildActiveDays = (calendarRow: GTFSCalendarRow): string[] => {
  return DAY_ORDER.filter(({ key }) => calendarRow[key] === '1').map(({ label }) => label);
};

/**
 * Persisted service-calendar fields derived from a selected service_id's
 * GTFS `calendar` row + `calendar_dates` exceptions. Any field is omitted when
 * the underlying GTFS data is absent (so existing routes stay undefined).
 */
export interface GTFSServiceCalendar {
  serviceDays?: ServiceDaysMask;
  serviceDateRange?: { startDate: string; endDate: string };
  serviceExceptions?: Array<{ date: string; type: ServiceExceptionType }>;
}

const toServiceDaysMask = (calendarRow: GTFSCalendarRow): ServiceDaysMask => ({
  monday: calendarRow.monday === '1',
  tuesday: calendarRow.tuesday === '1',
  wednesday: calendarRow.wednesday === '1',
  thursday: calendarRow.thursday === '1',
  friday: calendarRow.friday === '1',
  saturday: calendarRow.saturday === '1',
  sunday: calendarRow.sunday === '1',
});

/**
 * Build the persisted service calendar for the SELECTED service_id from the
 * parsed GTFS `calendar` row and `calendar_dates` exceptions. Returns an empty
 * object (all fields undefined) when no calendar data is present for the id.
 */
export const buildServiceCalendarForServiceId = (
  serviceId: string | null,
  calendarRows: GTFSCalendarRow[] = [],
  calendarDateRows: GTFSCalendarDateRow[] = [],
): GTFSServiceCalendar => {
  if (!serviceId) {
    return {};
  }

  const result: GTFSServiceCalendar = {};

  const calendarRow = calendarRows.find((row) => row.service_id === serviceId);
  if (calendarRow) {
    result.serviceDays = toServiceDaysMask(calendarRow);
    if (calendarRow.start_date && calendarRow.end_date) {
      result.serviceDateRange = {
        startDate: calendarRow.start_date,
        endDate: calendarRow.end_date,
      };
    }
  }

  const exceptions = calendarDateRows
    .filter((entry) => entry.service_id === serviceId)
    .map((entry) => ({
      date: entry.date,
      type: (entry.exception_type === '2' ? 'removed' : 'added') as ServiceExceptionType,
    }));
  if (exceptions.length > 0) {
    result.serviceExceptions = exceptions;
  }

  return result;
};

export const summarizeServiceWindows = (
  calendarRows: GTFSCalendarRow[] = [],
  calendarDateRows: GTFSCalendarDateRow[] = [],
): ServiceWindowSummary[] => {
  const additions = new Map<string, number>();
  const removals = new Map<string, number>();

  calendarDateRows.forEach((entry) => {
    if (entry.exception_type === '1') {
      additions.set(entry.service_id, (additions.get(entry.service_id) || 0) + 1);
    } else if (entry.exception_type === '2') {
      removals.set(entry.service_id, (removals.get(entry.service_id) || 0) + 1);
    }
  });

  return calendarRows.map((row) => ({
    serviceId: row.service_id,
    label: `${toPreference(row) === 'custom' ? 'Custom' : toPreference(row)} (${row.service_id})`,
    activeDays: buildActiveDays(row),
    preference: toPreference(row),
    startDate: row.start_date,
    endDate: row.end_date,
    addedDates: additions.get(row.service_id),
    removedDates: removals.get(row.service_id),
  }));
};

const pickServiceIdForPreference = (
  preference: ServicePreference,
  summaries: ServiceWindowSummary[],
  trips: GTFSTripRow[],
): string | null => {
  const exactMatch = summaries.find((summary) => summary.preference === preference);
  if (exactMatch) return exactMatch.serviceId;

  const serviceUsage = new Map<string, number>();
  trips.forEach((trip) => {
    serviceUsage.set(trip.service_id, (serviceUsage.get(trip.service_id) || 0) + 1);
  });

  if (summaries.length === 0 && serviceUsage.size > 0) {
    return Array.from(serviceUsage.entries()).sort((a, b) => b[1] - a[1])[0][0];
  }

  const fallback = summaries.find((summary) => summary.preference === 'custom');
  return fallback ? fallback.serviceId : serviceUsage.keys().next().value ?? null;
};

const buildStopMap = (stops: GTFSStopsRow[]): Map<string, FixedRouteStop> => {
  const stopMap = new Map<string, FixedRouteStop>();

  stops.forEach((stop) => {
    const lat = parseLatLng(stop.stop_lat);
    const lon = parseLatLng(stop.stop_lon);
    if (lat === null || lon === null) {
      return;
    }
    const id = `gtfs-stop-${stop.stop_id}`;
    stopMap.set(id, {
      id,
      name: stop.stop_name || `Stop ${stop.stop_id}`,
      coordinates: { lat, lng: lon },
    });
  });

  return stopMap;
};

const buildStopLookupByOriginalId = (stops: GTFSStopsRow[]): Map<string, FixedRouteStop> => {
  const map = new Map<string, FixedRouteStop>();
  stops.forEach((stop) => {
    const lat = parseLatLng(stop.stop_lat);
    const lon = parseLatLng(stop.stop_lon);
    if (lat === null || lon === null) return;
    const fixedStop: FixedRouteStop = {
      id: `gtfs-stop-${stop.stop_id}`,
      name: stop.stop_name || `Stop ${stop.stop_id}`,
      coordinates: { lat, lng: lon },
    };
    map.set(stop.stop_id, fixedStop);
  });
  return map;
};

const buildShapesMap = (shapes: GTFSShapeRow[]): Map<string, [number, number][]> => {
  const intermediate = new Map<string, Array<{ seq: number; coord: [number, number] }>>();
  shapes.forEach((shapeRow) => {
    const lat = parseLatLng(shapeRow.shape_pt_lat);
    const lon = parseLatLng(shapeRow.shape_pt_lon);
    if (lat === null || lon === null) return;
    const seq = Number(shapeRow.shape_pt_sequence);
    const list = intermediate.get(shapeRow.shape_id) || [];
    list.push({ seq: Number.isNaN(seq) ? list.length : seq, coord: [lat, lon] });
    intermediate.set(shapeRow.shape_id, list);
  });

  const shapesById = new Map<string, [number, number][]>();
  intermediate.forEach((list, shapeId) => {
    list.sort((a, b) => a.seq - b.seq);
    shapesById.set(
      shapeId,
      list.map((entry) => entry.coord),
    );
  });

  return shapesById;
};

/**
 * Build a resolver from `routes.agency_id` → captured {@link GtfsAgency}.
 * Per the GTFS spec, `agency.txt` may omit `agency_id` (and `routes.agency_id`
 * may be blank) when the feed has exactly one agency — so when a route has no
 * usable id and there is exactly one agency row, fall back to that row.
 */
const buildAgencyResolver = (
  agencyRows: GTFSAgencyRow[] = [],
): ((agencyId?: string) => GtfsAgency | undefined) => {
  const toAgency = (row: GTFSAgencyRow): GtfsAgency | undefined => {
    const name = (row.agency_name || '').trim();
    if (!name) return undefined;
    return {
      agencyId: (row.agency_id || '').trim() || name,
      name,
      url: (row.agency_url || '').trim(),
      timezone: (row.agency_timezone || '').trim(),
      lang: (row.agency_lang || '').trim() || undefined,
    };
  };

  const byId = new Map<string, GtfsAgency>();
  agencyRows.forEach((row) => {
    const agency = toAgency(row);
    if (agency) byId.set(agency.agencyId, agency);
  });

  const soleAgency = byId.size === 1 ? Array.from(byId.values())[0] : undefined;

  return (agencyId?: string): GtfsAgency | undefined => {
    const id = (agencyId || '').trim();
    if (id && byId.has(id)) return byId.get(id);
    return soleAgency;
  };
};

const parseRouteType = (value?: string): number | undefined => {
  if (value === undefined || value === null || value === '') return undefined;
  const num = Number(value);
  return Number.isNaN(num) ? undefined : num;
};

const buildStopTimesMap = (stopTimes: GTFSStopTimeRow[]): Map<string, GTFSStopTimeRow[]> => {
  const map = new Map<string, GTFSStopTimeRow[]>();

  stopTimes.forEach((row) => {
    const tripId = row.trip_id?.toString().trim();
    const stopId = row.stop_id?.toString().trim();
    const stopSequence = Number(row.stop_sequence);
    if (!tripId || !stopId || Number.isNaN(stopSequence)) {
      return;
    }
    const tripTimes = map.get(tripId) || [];
    tripTimes.push({
      ...row,
      trip_id: tripId,
      stop_id: stopId,
      stop_sequence: String(stopSequence),
    });
    map.set(tripId, tripTimes);
  });

  map.forEach((times, tripId) => {
    times.sort((a, b) => Number(a.stop_sequence) - Number(b.stop_sequence));
    map.set(tripId, times);
  });

  return map;
};

const CIRCULATOR_TERMINAL_DISTANCE_THRESHOLD_METERS = 250;
const EARTH_RADIUS_METERS = 6371000;

const parseShapeDistTraveled = (value?: string): number | null => {
  if (value == null || value.trim() === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Closest point on segment AB to P in a local equirectangular frame.
 * Returns t in [0,1] along AB and planar distance in meters from P to that point.
 */
const closestPointOnSegment = (
  point: [number, number],
  a: [number, number],
  b: [number, number],
): { t: number; distMeters: number } => {
  const lat0 = (((a[0] + b[0] + point[0]) / 3) * Math.PI) / 180;
  const toXY = (c: [number, number]): [number, number] => {
    const x = (((c[1] - a[1]) * Math.PI) / 180) * Math.cos(lat0) * EARTH_RADIUS_METERS;
    const y = (((c[0] - a[0]) * Math.PI) / 180) * EARTH_RADIUS_METERS;
    return [x, y];
  };
  const p = toXY(point);
  const origin = toXY(a);
  const end = toXY(b);
  const abx = end[0] - origin[0];
  const aby = end[1] - origin[1];
  const apx = p[0] - origin[0];
  const apy = p[1] - origin[1];
  const ab2 = abx * abx + aby * aby;
  const t = ab2 === 0 ? 0 : Math.max(0, Math.min(1, (apx * abx + apy * aby) / ab2));
  const qx = origin[0] + t * abx;
  const qy = origin[1] + t * aby;
  return { t, distMeters: Math.hypot(p[0] - qx, p[1] - qy) };
};

const polylineLengthMeters = (coords: [number, number][]): number => {
  let total = 0;
  for (let i = 0; i < coords.length - 1; i += 1) {
    total += getDistanceMeters(coords[i], coords[i + 1]);
  }
  return total;
};

/**
 * Distance along the polyline from its first vertex to the nearest point on the
 * polyline to `point` (meters).
 */
const distanceAlongPolylineMeters = (
  point: [number, number],
  coords: [number, number][],
): number => {
  if (coords.length < 2) return 0;

  let bestDistToPoint = Number.POSITIVE_INFINITY;
  let bestAlong = 0;
  let alongSoFar = 0;

  for (let i = 0; i < coords.length - 1; i += 1) {
    const a = coords[i];
    const b = coords[i + 1];
    const segLen = getDistanceMeters(a, b);
    const { t, distMeters } = closestPointOnSegment(point, a, b);
    const along = alongSoFar + t * segLen;
    if (distMeters < bestDistToPoint) {
      bestDistToPoint = distMeters;
      bestAlong = along;
    }
    alongSoFar += segLen;
  }
  return bestAlong;
};

/**
 * Monotonic distance-along-shape for each stop in trip order, so consecutive
 * segment lengths stay non-negative even when nearest-point projection jitters.
 * Closing stop equal to the first (circulator) is pinned to full shape length.
 */
const computeMonotonicAlongMeters = (
  stopTimes: GTFSStopTimeRow[],
  stopLookupByOriginalId: Map<string, FixedRouteStop>,
  shapeCoords: [number, number][],
): number[] => {
  const totalLen = polylineLengthMeters(shapeCoords);
  const alongs: number[] = [];
  for (let i = 0; i < stopTimes.length; i += 1) {
    const stop = stopLookupByOriginalId.get(stopTimes[i].stop_id);
    if (!stop) {
      alongs.push(i > 0 ? alongs[i - 1] : 0);
      continue;
    }
    const point: [number, number] = [stop.coordinates.lat, stop.coordinates.lng];
    let along = distanceAlongPolylineMeters(point, shapeCoords);
    if (
      i === stopTimes.length - 1 &&
      stopTimes.length > 1 &&
      stopTimes[0].stop_id === stopTimes[i].stop_id &&
      totalLen > 0
    ) {
      along = totalLen;
    } else if (i > 0) {
      along = Math.max(along, alongs[i - 1]);
    }
    alongs.push(along);
  }
  return alongs;
};

const buildRouteGeometryHash = (
  stopConfigs: FixedRouteStopConfig[],
  stopLookup: Map<string, FixedRouteStop>,
  isCirculator: boolean,
  isBusRoute: boolean,
): string => {
  // Delegate to the CANONICAL hash (the producer/consumer contract documented on
  // computeRouteGeometryHash). The map sync re-derives this same hash to decide
  // whether a route's cached geometry is still valid; if our hash diverges, every
  // imported GTFS route looks "stale" and gets re-routed via OSRM on import —
  // slow, and it discards the real (road-following) GTFS shape we just built.
  // GTFS imports never carry freehand/waypoints, so those fields are absent here.
  const routeStops = stopConfigs
    .map((config) => stopLookup.get(config.stopId))
    .filter((stop): stop is FixedRouteStop => Boolean(stop));
  return computeRouteGeometryHash(
    { isCirculator, isBusRoute } as unknown as FixedRoute,
    routeStops,
  );
};

const buildGeometryForTrip = (
  trip: GTFSTripRow,
  stopConfigs: FixedRouteStopConfig[],
  stopLookup: Map<string, FixedRouteStop>,
  shapesById: Map<string, [number, number][]>,
  isCirculator: boolean,
  isBusRoute: boolean,
): RouteGeometry => {
  const shapeCoords = trip.shape_id ? shapesById.get(trip.shape_id) : undefined;

  const coordinates =
    shapeCoords && shapeCoords.length > 1
      ? shapeCoords
      : stopConfigs
          .map((config) => stopLookup.get(config.stopId))
          .filter((stop): stop is FixedRouteStop => Boolean(stop))
          .map((stop) => [stop.coordinates.lat, stop.coordinates.lng] as [number, number]);

  return {
    coordinates,
    calculatedAt: Date.now(),
    stopsHash: buildRouteGeometryHash(stopConfigs, stopLookup, isCirculator, isBusRoute),
  };
};

const resolveShapeDistScale = (
  stopTimes: GTFSStopTimeRow[],
  referenceMeters: number,
): 1 | 1000 | null => {
  /**
   * GTFS requires meters, but some feeds (incl. UK BODS/TransXChange exports)
   * put kilometres in shape_dist_traveled. Compare the trip span to a geometry
   * reference and accept meters, scale km→m, or distrust the field.
   */
  let span: number | null = null;
  const first = parseShapeDistTraveled(stopTimes[0]?.shape_dist_traveled);
  const last = parseShapeDistTraveled(stopTimes[stopTimes.length - 1]?.shape_dist_traveled);
  if (first !== null && last !== null && last >= first && stopTimes.length >= 2) {
    span = last - first;
  } else {
    let sum = 0;
    let any = false;
    for (let i = 0; i < stopTimes.length - 1; i += 1) {
      const a = parseShapeDistTraveled(stopTimes[i].shape_dist_traveled);
      const b = parseShapeDistTraveled(stopTimes[i + 1].shape_dist_traveled);
      if (a !== null && b !== null && b >= a) {
        sum += b - a;
        any = true;
      }
    }
    if (any) span = sum;
  }

  if (span === null || span <= 0) return null;
  if (referenceMeters <= 0) return 1;

  const withinFactor = (candidate: number, reference: number, factor: number): boolean => {
    const ratio = candidate / reference;
    return ratio >= 1 / factor && ratio <= factor;
  };

  if (withinFactor(span, referenceMeters, 2.5)) return 1;
  if (withinFactor(span * 1000, referenceMeters, 2.5)) return 1000;
  return null;
};

const crowFlyPathMeters = (
  stopTimes: GTFSStopTimeRow[],
  stopLookupByOriginalId: Map<string, FixedRouteStop>,
): number => {
  let total = 0;
  for (let i = 0; i < stopTimes.length - 1; i += 1) {
    const fromStop = stopLookupByOriginalId.get(stopTimes[i].stop_id);
    const toStop = stopLookupByOriginalId.get(stopTimes[i + 1].stop_id);
    if (!fromStop || !toStop) continue;
    total += getDistanceMeters(
      [fromStop.coordinates.lat, fromStop.coordinates.lng],
      [toStop.coordinates.lat, toStop.coordinates.lng],
    );
  }
  return total;
};

const resolveSegmentDistanceMeters = (
  current: GTFSStopTimeRow,
  next: GTFSStopTimeRow,
  stopLookupByOriginalId: Map<string, FixedRouteStop>,
  alongShapeDeltaMeters: number | null,
  shapeDistScale: 1 | 1000 | null,
): number => {
  if (shapeDistScale !== null) {
    const fromDist = parseShapeDistTraveled(current.shape_dist_traveled);
    const toDist = parseShapeDistTraveled(next.shape_dist_traveled);
    if (fromDist !== null && toDist !== null && toDist >= fromDist) {
      return Math.round((toDist - fromDist) * shapeDistScale);
    }
  }

  if (alongShapeDeltaMeters !== null && alongShapeDeltaMeters >= 0) {
    return Math.round(alongShapeDeltaMeters);
  }

  const fromStop = stopLookupByOriginalId.get(current.stop_id);
  const toStop = stopLookupByOriginalId.get(next.stop_id);
  if (!fromStop || !toStop) return 0;

  return Math.round(
    getDistanceMeters(
      [fromStop.coordinates.lat, fromStop.coordinates.lng],
      [toStop.coordinates.lat, toStop.coordinates.lng],
    ),
  );
};

const buildSegments = (
  stopTimes: GTFSStopTimeRow[],
  stopLookupByOriginalId: Map<string, FixedRouteStop>,
  shapeCoords?: [number, number][],
): FixedRouteSegment[] => {
  const alongMeters =
    shapeCoords && shapeCoords.length > 1
      ? computeMonotonicAlongMeters(stopTimes, stopLookupByOriginalId, shapeCoords)
      : null;

  const referenceMeters =
    alongMeters !== null && alongMeters.length >= 2
      ? alongMeters[alongMeters.length - 1] - alongMeters[0]
      : crowFlyPathMeters(stopTimes, stopLookupByOriginalId);
  const shapeDistScale = resolveShapeDistScale(stopTimes, referenceMeters);

  const segments: FixedRouteSegment[] = [];
  for (let i = 0; i < stopTimes.length - 1; i += 1) {
    const current = stopTimes[i];
    const next = stopTimes[i + 1];
    const departure = parseTimeToMinutes(current.departure_time) ?? parseTimeToMinutes(current.arrival_time);
    const arrival = parseTimeToMinutes(next.arrival_time) ?? parseTimeToMinutes(next.departure_time);
    const travelTime =
      arrival !== null && departure !== null
        ? Math.max(normalizeWraparoundTravelMinutes(departure, arrival), 1)
        : 1;
    const alongDelta =
      alongMeters !== null ? alongMeters[i + 1] - alongMeters[i] : null;
    segments.push({
      fromStopId: `gtfs-stop-${current.stop_id}`,
      toStopId: `gtfs-stop-${next.stop_id}`,
      travelTimeMinutes: travelTime,
      distanceMeters: resolveSegmentDistanceMeters(
        current,
        next,
        stopLookupByOriginalId,
        alongDelta,
        shapeDistScale,
      ),
    });
  }
  return segments;
};

const isTripCirculator = (
  tripStopTimes: GTFSStopTimeRow[],
  stopLookupByOriginalId: Map<string, FixedRouteStop>,
): boolean => {
  if (tripStopTimes.length < 2) return false;
  const first = tripStopTimes[0];
  const last = tripStopTimes[tripStopTimes.length - 1];

  if (first.stop_id === last.stop_id) {
    return true;
  }

  const firstStop = stopLookupByOriginalId.get(first.stop_id);
  const lastStop = stopLookupByOriginalId.get(last.stop_id);
  if (!firstStop || !lastStop) {
    return false;
  }

  const distance = getDistanceMeters(
    [firstStop.coordinates.lat, firstStop.coordinates.lng],
    [lastStop.coordinates.lat, lastStop.coordinates.lng],
  );
  return distance <= CIRCULATOR_TERMINAL_DISTANCE_THRESHOLD_METERS;
};

const normalizeMinutesToDay = (minutes: number): number => ((minutes % (24 * 60)) + 24 * 60) % (24 * 60);

const getTripStartMinutes = (tripStopTimes: GTFSStopTimeRow[]): number | null => {
  if (tripStopTimes.length === 0) return null;
  return parseTimeToMinutes(tripStopTimes[0].departure_time) ?? parseTimeToMinutes(tripStopTimes[0].arrival_time);
};

const getTripRuntimeMinutes = (tripStopTimes: GTFSStopTimeRow[]): number | null => {
  if (tripStopTimes.length < 2) return null;
  const start = getTripStartMinutes(tripStopTimes);
  const end =
    parseTimeToMinutes(tripStopTimes[tripStopTimes.length - 1].arrival_time) ??
    parseTimeToMinutes(tripStopTimes[tripStopTimes.length - 1].departure_time);
  if (start === null || end === null) return null;
  return Math.max(normalizeWraparoundTravelMinutes(start, end), 1);
};

const median = (values: number[]): number | null => {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 0) {
    return (sorted[mid - 1] + sorted[mid]) / 2;
  }
  return sorted[mid];
};

const deriveCirculatorVehicleCount = (tripStopTimesList: GTFSStopTimeRow[][]): number => {
  const runtimes = tripStopTimesList
    .map((times) => getTripRuntimeMinutes(times))
    .filter((runtime): runtime is number => runtime !== null && runtime > 0);

  const startTimes = tripStopTimesList
    .map((times) => getTripStartMinutes(times))
    .filter((start): start is number => start !== null)
    .map((start) => normalizeMinutesToDay(start))
    .sort((a, b) => a - b);

  const runtimeMedian = median(runtimes);
  if (!runtimeMedian) {
    return 1;
  }

  if (startTimes.length < 2) {
    return 1;
  }

  const headwayDiffs: number[] = [];
  for (let i = 1; i < startTimes.length; i += 1) {
    const diff = startTimes[i] - startTimes[i - 1];
    if (diff > 0) {
      headwayDiffs.push(diff);
    }
  }
  const wrapDiff = (startTimes[0] + 24 * 60) - startTimes[startTimes.length - 1];
  if (wrapDiff > 0) {
    headwayDiffs.push(wrapDiff);
  }

  const headwayMedian = median(headwayDiffs);
  if (!headwayMedian || headwayMedian <= 0) {
    return 1;
  }

  return Math.max(1, Math.min(20, Math.ceil(runtimeMedian / headwayMedian)));
};

const buildStopConfigs = (stopTimes: GTFSStopTimeRow[]): FixedRouteStopConfig[] => {
  return stopTimes.map((stopTime) => {
    const arrival = parseTimeToMinutes(stopTime.arrival_time);
    const departure = parseTimeToMinutes(stopTime.departure_time);
    const waitTime = arrival !== null && departure !== null ? Math.max(departure - arrival, 0) : 0;
    return {
      stopId: `gtfs-stop-${stopTime.stop_id}`,
      waitTimeMinutes: waitTime,
    };
  });
};

const chooseTripForRoute = (
  routeId: string,
  serviceId: string | null,
  trips: GTFSTripRow[],
  stopTimesMap: Map<string, GTFSStopTimeRow[]>,
): { trip: GTFSTripRow | null; stopTimes: GTFSStopTimeRow[] } => {
  const candidateTrips = trips.filter(
    (trip) => trip.route_id === routeId && (!serviceId || trip.service_id === serviceId),
  );

  if (candidateTrips.length === 0) {
    return { trip: null, stopTimes: [] };
  }

  const scored = candidateTrips
    .map((trip) => {
      const stopTimes = stopTimesMap.get(trip.trip_id) || [];
      if (stopTimes.length === 0) {
        return { trip, stopTimes, start: Number.MAX_SAFE_INTEGER };
      }
      const firstDeparture =
        parseTimeToMinutes(stopTimes[0].departure_time) ?? parseTimeToMinutes(stopTimes[0].arrival_time) ?? Number.MAX_SAFE_INTEGER;
      return { trip, stopTimes, start: firstDeparture };
    })
    .filter(({ stopTimes }) => stopTimes.length >= 2)
    .sort((a, b) => a.start - b.start);

  if (scored.length === 0) {
    return { trip: null, stopTimes: [] };
  }

  return { trip: scored[0].trip, stopTimes: scored[0].stopTimes };
};

type DirectionBucket = '0' | '1' | 'none';

const normalizeDirectionBucket = (directionId: string | undefined): DirectionBucket => {
  const raw = (directionId ?? '').trim();
  if (raw === '1') return '1';
  if (raw === '0') return '0';
  return 'none';
};

/**
 * Decide how to bucket trips for a GTFS route_id.
 * - All blank direction_id → single undirected bucket (backward compatible).
 * - Any 0/1 present → split into outbound (0 + blank) and inbound (1); circulators stay unsplit.
 */
const partitionTripsByDirection = (
  trips: GTFSTripRow[],
  stopTimesMap: Map<string, GTFSStopTimeRow[]>,
  stopLookupByOriginalId: Map<string, FixedRouteStop>,
): { undirected: GTFSTripRow[] | null; outbound: GTFSTripRow[]; inbound: GTFSTripRow[] } => {
  const routeTripStopTimes = trips
    .map((trip) => stopTimesMap.get(trip.trip_id) || [])
    .filter((times) => times.length >= 2);
  const circulatorTripStopTimes = routeTripStopTimes.filter((times) =>
    isTripCirculator(times, stopLookupByOriginalId),
  );
  const isCirculator =
    circulatorTripStopTimes.length > 0 &&
    circulatorTripStopTimes.length >= Math.ceil(routeTripStopTimes.length / 2);

  if (isCirculator) {
    return { undirected: trips, outbound: [], inbound: [] };
  }

  const hasExplicitDirection = trips.some((trip) => {
    const bucket = normalizeDirectionBucket(trip.direction_id);
    return bucket === '0' || bucket === '1';
  });

  if (!hasExplicitDirection) {
    return { undirected: trips, outbound: [], inbound: [] };
  }

  const outbound: GTFSTripRow[] = [];
  const inbound: GTFSTripRow[] = [];
  for (const trip of trips) {
    if (normalizeDirectionBucket(trip.direction_id) === '1') {
      inbound.push(trip);
    } else {
      // GTFS 0 or blank when mixed → outbound
      outbound.push(trip);
    }
  }
  return { undirected: null, outbound, inbound };
};

interface BuildDirectionRouteArgs {
  routeId: string;
  route: GTFSRoutesRow;
  tripsToProcess: GTFSTripRow[];
  stopTimesMap: Map<string, GTFSStopTimeRow[]>;
  stopLookupByOriginalId: Map<string, FixedRouteStop>;
  stopLookupByFixedId: Map<string, FixedRouteStop>;
  shapesById: Map<string, [number, number][]>;
  includedStopIds: Set<string>;
  warnings: string[];
  serviceCalendar: ReturnType<typeof buildServiceCalendarForServiceId>;
  resolveAgency: (agencyId: string | undefined) => GtfsAgency | undefined;
  routeColor: string;
  /** Internal id suffix: '' | '-d0' | '-d1' */
  idSuffix: string;
  directionId?: 'outbound' | 'inbound';
  /** When both directions exist, append (Outbound)/(Inbound) to the display name. */
  nameSuffix?: ' (Outbound)' | ' (Inbound)';
}

const buildFixedRouteFromTrips = (args: BuildDirectionRouteArgs): FixedRoute | null => {
  const {
    routeId,
    route,
    tripsToProcess,
    stopTimesMap,
    stopLookupByOriginalId,
    stopLookupByFixedId,
    shapesById,
    includedStopIds,
    warnings,
    serviceCalendar,
    resolveAgency,
    routeColor,
    idSuffix,
    directionId,
    nameSuffix,
  } = args;

  // Dominant shape within this direction bucket
  const shapeUsage = new Map<string, number>();
  for (const trip of tripsToProcess) {
    const sid = (trip.shape_id || '').trim();
    if (sid) {
      shapeUsage.set(sid, (shapeUsage.get(sid) || 0) + 1);
    }
  }
  let dominantShapeId = '';
  let dominantShapeCount = 0;
  shapeUsage.forEach((count, sid) => {
    if (count > dominantShapeCount) {
      dominantShapeCount = count;
      dominantShapeId = sid;
    }
  });

  let representativeTrip: GTFSTripRow | null = null;
  let representativeStopTimes: GTFSStopTimeRow[] = [];
  let representativeScore = -1;
  let earliestStart: number | null = null;
  let latestEnd: number | null = null;

  for (const trip of tripsToProcess) {
    const stopTimes = stopTimesMap.get(trip.trip_id) || [];
    if (stopTimes.length < 2) continue;

    const firstDeparture = parseTimeToMinutes(stopTimes[0].departure_time) ?? parseTimeToMinutes(stopTimes[0].arrival_time);
    const lastArrival =
      parseTimeToMinutes(stopTimes[stopTimes.length - 1].arrival_time) ??
      parseTimeToMinutes(stopTimes[stopTimes.length - 1].departure_time);

    if (firstDeparture !== null) {
      if (earliestStart === null || firstDeparture < earliestStart) {
        earliestStart = firstDeparture;
      }
    }
    if (lastArrival !== null) {
      if (latestEnd === null || lastArrival > latestEnd) {
        latestEnd = lastArrival;
      }
    }

    let validTimedSegments = 0;
    for (let i = 0; i < stopTimes.length - 1; i += 1) {
      const dep = parseTimeToMinutes(stopTimes[i].departure_time) ?? parseTimeToMinutes(stopTimes[i].arrival_time);
      const arr = parseTimeToMinutes(stopTimes[i + 1].arrival_time) ?? parseTimeToMinutes(stopTimes[i + 1].departure_time);
      if (dep !== null && arr !== null) {
        const delta = normalizeWraparoundTravelMinutes(dep, arr);
        if (delta >= 0) validTimedSegments += 1;
      }
    }

    const score = validTimedSegments;
    const candidateIsDominant = Boolean(dominantShapeId && (trip.shape_id || '').trim() === dominantShapeId);
    const representativeIsDominant = Boolean(
      dominantShapeId && representativeTrip && (representativeTrip.shape_id || '').trim() === dominantShapeId,
    );
    const representativeHasShape = Boolean(representativeTrip?.shape_id);
    const candidateHasShape = Boolean(trip.shape_id);
    const shouldReplace =
      !representativeTrip ||
      (candidateIsDominant && !representativeIsDominant) ||
      (candidateIsDominant === representativeIsDominant && score > representativeScore) ||
      (candidateIsDominant === representativeIsDominant &&
        score === representativeScore &&
        candidateHasShape &&
        !representativeHasShape);

    if (shouldReplace) {
      representativeTrip = trip;
      representativeStopTimes = stopTimes;
      representativeScore = score;
    }
  }

  const label = directionId ? `${routeId}/${directionId}` : routeId;
  if (!representativeTrip || representativeStopTimes.length < 2) {
    warnings.push(`Skipped route ${label}: no valid trips with stop_times`);
    return null;
  }

  const missingStop = representativeStopTimes.some((stopTime) => !stopLookupByOriginalId.get(stopTime.stop_id));
  if (missingStop) {
    warnings.push(`Skipping route ${label}: missing stop metadata`);
    return null;
  }

  const stopConfigs = buildStopConfigs(representativeStopTimes);
  stopConfigs.forEach((config) => includedStopIds.add(config.stopId));

  const shapeCoords = representativeTrip.shape_id
    ? shapesById.get(representativeTrip.shape_id)
    : undefined;
  const segments = buildSegments(
    representativeStopTimes,
    stopLookupByOriginalId,
    shapeCoords,
  );
  if (segments.length !== stopConfigs.length - 1) {
    warnings.push(`Segments mismatch for route ${label}`);
    return null;
  }

  const routeTripStopTimes = tripsToProcess
    .map((trip) => stopTimesMap.get(trip.trip_id) || [])
    .filter((times) => times.length >= 2);
  const circulatorTripStopTimes = routeTripStopTimes.filter((times) =>
    isTripCirculator(times, stopLookupByOriginalId),
  );
  const isCirculator =
    circulatorTripStopTimes.length > 0 &&
    circulatorTripStopTimes.length >= Math.ceil(routeTripStopTimes.length / 2);
  const isBusRoute = false;
  const geometry = buildGeometryForTrip(
    representativeTrip,
    stopConfigs,
    stopLookupByFixedId,
    shapesById,
    isCirculator,
    isBusRoute,
  );

  const baseName = route.route_long_name || route.route_short_name || `Route ${routeId}`;
  const name = nameSuffix ? `${baseName}${nameSuffix}` : baseName;
  const shortName = route.route_long_name && route.route_short_name ? route.route_short_name : undefined;

  const startTimeStr = earliestStart !== null ? minutesFormatter(earliestStart) : '00:00';
  const endTimeStr = latestEnd !== null ? minutesFormatter(latestEnd) : startTimeStr;

  const tripSchedules: Array<{ startTimeMinutes: number; stopArrivalTimesMinutes: number[] }> = [];
  const vehicleCount = isCirculator ? deriveCirculatorVehicleCount(circulatorTripStopTimes) : undefined;

  if (!isCirculator) {
    const stopIndexMap = new Map<string, number>();
    stopConfigs.forEach((config, index) => {
      stopIndexMap.set(config.stopId, index);
    });

    for (const trip of tripsToProcess) {
      const tripStopTimes = stopTimesMap.get(trip.trip_id) || [];
      if (tripStopTimes.length < 2) continue;

      const tripStartMinutes = parseTimeToMinutes(tripStopTimes[0].departure_time) ??
        parseTimeToMinutes(tripStopTimes[0].arrival_time);
      if (tripStartMinutes === null) continue;

      const stopArrivalTimes: number[] = new Array(stopConfigs.length).fill(-1);

      for (const stopTime of tripStopTimes) {
        const stopId = `gtfs-stop-${stopTime.stop_id}`;
        const routeStopIndex = stopIndexMap.get(stopId);

        if (routeStopIndex !== undefined) {
          const arrivalMinutes = parseTimeToMinutes(stopTime.arrival_time) ??
            parseTimeToMinutes(stopTime.departure_time);
          if (arrivalMinutes !== null) {
            const relativeArrival = arrivalMinutes >= tripStartMinutes
              ? arrivalMinutes - tripStartMinutes
              : (arrivalMinutes + 24 * 60) - tripStartMinutes;
            stopArrivalTimes[routeStopIndex] = relativeArrival;
          }
        }
      }

      if (stopArrivalTimes[0] >= 0 && stopArrivalTimes[stopArrivalTimes.length - 1] >= 0) {
        tripSchedules.push({
          startTimeMinutes: tripStartMinutes % (24 * 60),
          stopArrivalTimesMinutes: stopArrivalTimes,
        });
      }
    }

    tripSchedules.sort((a, b) => a.startTimeMinutes - b.startTimeMinutes);
  }

  const gtfsRouteType = parseRouteType(route.route_type);
  const routeDesc = route.route_desc?.trim() || undefined;
  const routeTextColor = sanitizeColor(route.route_text_color);
  const tripHeadsign = representativeTrip.trip_headsign?.trim() || undefined;
  const gtfsAgency = resolveAgency(route.agency_id);
  const fixedId = `gtfs-route-${routeId}${idSuffix}`;

  return {
    id: fixedId,
    name,
    shortName,
    startTime: startTimeStr,
    endTime: endTimeStr,
    stops: stopConfigs,
    segments,
    geometry,
    color: routeColor,
    width: 6,
    isCirculator,
    vehicleCount,
    isBusRoute,
    lineId: `${fixedId}-line`,
    gtfsRouteId: routeId,
    gtfsRouteType,
    routeDesc,
    routeTextColor,
    tripHeadsign,
    gtfsAgency,
    tripSchedules: tripSchedules.length > 0 ? tripSchedules : undefined,
    serviceDays: serviceCalendar.serviceDays,
    serviceDateRange: serviceCalendar.serviceDateRange,
    serviceExceptions: serviceCalendar.serviceExceptions,
    ...(directionId ? { directionId } : {}),
  };
};

export const convertGTFSFeedToFixedRoutes = (
  data: GTFSFeedData,
  options: GTFSConversionOptions = {},
): GTFSConverterResult => {
  const warnings: string[] = [];
  const stopLookupByOriginalId = buildStopLookupByOriginalId(data.stops);
  const stopLookupByFixedId = new Map<string, FixedRouteStop>();
  stopLookupByOriginalId.forEach((stop) => {
    stopLookupByFixedId.set(stop.id, stop);
  });
  const shapesById = buildShapesMap(data.shapes);
  const stopTimesMap = buildStopTimesMap(data.stopTimes);
  const summaries = summarizeServiceWindows(data.calendar, data.calendarDates);
  const resolveAgency = buildAgencyResolver(data.agency);

  const requestedServiceId =
    options.targetServiceId ||
    (options.preferredService ? pickServiceIdForPreference(options.preferredService, summaries, data.trips) : null);
  let serviceId = requestedServiceId;

  let tripsForImport = data.trips;
  if (serviceId) {
    const matchingTrips = data.trips.filter((trip) => trip.service_id === serviceId);
    if (matchingTrips.length > 0) {
      tripsForImport = matchingTrips;
    } else {
      warnings.push(`Selected service ${serviceId} has no trips. Falling back to all services.`);
      serviceId = null;
    }
  }

  const serviceCalendar = buildServiceCalendarForServiceId(serviceId, data.calendar, data.calendarDates);

  const routes: FixedRoute[] = [];
  const includedStopIds = new Set<string>();
  const coveredGtfsRouteIds = new Set<string>();

  const routesById = new Map<string, GTFSRoutesRow>();
  data.routes.forEach((route) => {
    routesById.set(route.route_id, route);
  });

  const tripsByRouteId = new Map<string, GTFSTripRow[]>();
  tripsForImport.forEach((trip) => {
    const group = tripsByRouteId.get(trip.route_id) || [];
    group.push(trip);
    tripsByRouteId.set(trip.route_id, group);
  });

  const validRouteIds: string[] = [];
  tripsByRouteId.forEach((_trips, routeId) => {
    if (routesById.get(routeId)) {
      validRouteIds.push(routeId);
    }
  });

  const gtfsColors = new Map<string, string>();
  validRouteIds.forEach((routeId) => {
    const route = routesById.get(routeId);
    if (route) {
      const cleanedColor = sanitizeColor(route.route_color);
      if (cleanedColor) {
        gtfsColors.set(routeId, cleanedColor);
      }
    }
  });

  const uniqueGtfsColors = new Set(gtfsColors.values());
  const shouldIgnoreGtfsColors = uniqueGtfsColors.size <= 1 && validRouteIds.length > 1;

  if (shouldIgnoreGtfsColors) {
    console.log(`🎨 All GTFS routes have the same color (#${Array.from(uniqueGtfsColors)[0] || 'N/A'}), using generated palette instead`);
  }

  const colorPalette = generateRouteColors(Math.max(validRouteIds.length, 1));
  console.log(`🎨 Generated color palette with ${colorPalette.length} colors:`, colorPalette);
  let colorIndex = 0;

  const pickRouteColor = (routeId: string, route: GTFSRoutesRow): string => {
    if (!shouldIgnoreGtfsColors) {
      const cleanedColor = sanitizeColor(route.route_color);
      if (cleanedColor) {
        console.log(`  Route ${routeId} (${route.route_short_name || route.route_long_name}) using GTFS color: ${cleanedColor}`);
        return cleanedColor;
      }
    }
    const paletteIndex = colorIndex % colorPalette.length;
    const routeColor = colorPalette[paletteIndex];
    console.log(
      `  Route ${routeId} (${route.route_short_name || route.route_long_name}) assigned palette color[${paletteIndex}/${colorPalette.length}]: ${routeColor}${shouldIgnoreGtfsColors ? ' (GTFS color ignored - all routes have same color)' : ''}`,
    );
    colorIndex += 1;
    return routeColor;
  };

  tripsByRouteId.forEach((trips, routeId) => {
    const route = routesById.get(routeId);
    if (!route) {
      warnings.push(`Trips reference unknown route ${routeId}`);
      return;
    }

    const partition = partitionTripsByDirection(trips, stopTimesMap, stopLookupByOriginalId);
    const routeColor = pickRouteColor(routeId, route);

    const sharedArgs = {
      routeId,
      route,
      stopTimesMap,
      stopLookupByOriginalId,
      stopLookupByFixedId,
      shapesById,
      includedStopIds,
      warnings,
      serviceCalendar,
      resolveAgency,
      routeColor,
    };

    if (partition.undirected) {
      const built = buildFixedRouteFromTrips({
        ...sharedArgs,
        tripsToProcess: partition.undirected,
        idSuffix: '',
      });
      if (built) {
        routes.push(built);
        coveredGtfsRouteIds.add(routeId);
      }
      return;
    }

    const outboundTrips = partition.outbound;
    const inboundTrips = partition.inbound;
    const bothDirections = outboundTrips.length > 0 && inboundTrips.length > 0;

    let outboundRoute: FixedRoute | null = null;
    let inboundRoute: FixedRoute | null = null;

    if (outboundTrips.length > 0) {
      outboundRoute = buildFixedRouteFromTrips({
        ...sharedArgs,
        tripsToProcess: outboundTrips,
        idSuffix: bothDirections ? '-d0' : '',
        directionId: 'outbound',
        nameSuffix: bothDirections ? ' (Outbound)' : undefined,
      });
    }

    if (inboundTrips.length > 0) {
      inboundRoute = buildFixedRouteFromTrips({
        ...sharedArgs,
        tripsToProcess: inboundTrips,
        idSuffix: bothDirections ? '-d1' : '',
        directionId: 'inbound',
        nameSuffix: bothDirections ? ' (Inbound)' : undefined,
      });
    }

    if (outboundRoute && inboundRoute) {
      outboundRoute = { ...outboundRoute, linkedRouteId: inboundRoute.id };
      inboundRoute = { ...inboundRoute, linkedRouteId: outboundRoute.id };
      routes.push(outboundRoute, inboundRoute);
      coveredGtfsRouteIds.add(routeId);
    } else if (outboundRoute) {
      routes.push(outboundRoute);
      coveredGtfsRouteIds.add(routeId);
    } else if (inboundRoute) {
      routes.push(inboundRoute);
      coveredGtfsRouteIds.add(routeId);
    }
  });

  const uniqueStops = Array.from(stopLookupByFixedId.values()).filter((stop) => includedStopIds.has(stop.id));

  return {
    stops: uniqueStops,
    routes,
    serviceIdUsed: serviceId,
    warnings,
    stats: {
      totalRoutes: routes.length,
      totalStops: uniqueStops.length,
      skippedRoutes: data.routes.length - coveredGtfsRouteIds.size,
    },
  };
};

export const resolveServiceId = (
  data: GTFSFeedData,
  preference: ServicePreference | undefined,
  targetServiceId?: string,
): string | null => {
  if (targetServiceId) return targetServiceId;
  if (!preference) return null;
  const summaries = summarizeServiceWindows(data.calendar, data.calendarDates);
  return pickServiceIdForPreference(preference, summaries, data.trips);
};

/**
 * Load GTFS data from a zip file
 * Reuses the parsing logic from the existing GTFS processor
 */
export async function loadGTFSDataFromFile(file: File): Promise<GTFSFeedData> {
  const JSZip = (await import('jszip')).default;
  const Papa = (await import('papaparse')).default;

  const zip = new JSZip();
  const zipContent = await zip.loadAsync(file);
  const resolvedEntries = resolveGtfsZipEntries(zipContent, GTFS_CORE_FILES, GTFS_OPTIONAL_FILES);
  if (!resolvedEntries.hasRequired) {
    throw new Error(`Missing required GTFS files: ${resolvedEntries.missingRequired.join(', ')}`);
  }

  const parseCSV = async (content: string): Promise<any[]> => {
    return new Promise((resolve, reject) => {
      Papa.parse<any>(content, {
        header: true,
        complete: (results) => resolve(results.data),
        error: (error: Error) => reject(error),
      });
    });
  };

  const data: GTFSFeedData = {
    stops: [],
    routes: [],
    trips: [],
    stopTimes: [],
    shapes: [],
  };

  // Load required files
  for (const fileName of GTFS_CORE_FILES) {
    const zipPath = resolvedEntries.requiredPaths.get(fileName);
    if (!zipPath || !zipContent.files[zipPath]) {
      throw new Error(`Missing required GTFS files: ${fileName}`);
    }
    const content = await zipContent.files[zipPath].async('text');
    const parsed = await parseCSV(content);
    switch (fileName) {
      case 'stops.txt':
        data.stops = parsed;
        break;
      case 'routes.txt':
        data.routes = parsed;
        break;
      case 'trips.txt':
        data.trips = parsed;
        break;
      case 'stop_times.txt':
        data.stopTimes = parsed;
        break;
    }
  }

  // Load optional files
  for (const fileName of GTFS_OPTIONAL_FILES) {
    const zipPath = resolvedEntries.optionalPaths.get(fileName);
    if (zipPath && zipContent.files[zipPath]) {
      const content = await zipContent.files[zipPath].async('text');
      const parsed = await parseCSV(content);
      switch (fileName) {
        case 'agency.txt':
          data.agency = parsed;
          break;
        case 'shapes.txt':
          data.shapes = parsed;
          break;
        case 'calendar.txt':
          data.calendar = parsed;
          break;
        case 'calendar_dates.txt':
          data.calendarDates = parsed;
          break;
      }
    }
  }

  // Note: zipContent will be garbage collected after this function returns
  // The parsed data arrays are returned and will be used by the converter
  return data;
}

